/**
 * Residents Filter Dropdown Component
 *
 * Provides a FILTER button with a dropdown panel for the Residents tab,
 * matching the pattern used by the MDS/Care tabs (FilterDropdown).
 * Includes risk level, days since review, building, station, and show hidden filters.
 */

import { useState } from "react";
import { Filter, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FilterField } from "./FilterField";
import type { RiskLevel } from "../../types/residents";

interface FilterOption {
  value: string;
  label: string;
}

interface ResidentsFilterState {
  riskLevels: RiskLevel[];
  daysSinceReview: string;
  building: string;
  station: string[];
  showHidden: boolean;
}

interface ResidentsFilterDropdownProps {
  selectedRiskLevels: RiskLevel[];
  onRiskLevelsChange: (levels: RiskLevel[]) => void;
  daysSinceReview: string;
  onDaysSinceReviewChange: (value: string) => void;
  selectedBuilding: string;
  onBuildingChange: (value: string) => void;
  buildingOptions: FilterOption[];
  selectedStation: string[];
  onStationChange: (value: string[]) => void;
  stationOptions: FilterOption[];
  showHidden: boolean;
  onShowHiddenChange: (value: boolean) => void;
  isLoading?: boolean;
}

const RISK_LEVEL_OPTIONS: { value: RiskLevel; label: string }[] = [
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

/**
 * Counts the number of active filters applied on the Residents tab.
 */
export function getResidentsActiveFilterCount(
  riskLevels: RiskLevel[],
  daysSinceReview: string,
  building: string,
  station: string[],
  showHidden: boolean,
): number {
  let count = 0;
  if (riskLevels.length > 0) count++;
  if (daysSinceReview.trim()) count++;
  if (building && building !== "all") count++;
  if (station && station.length > 0) count++;
  if (showHidden) count++;
  return count;
}

export function ResidentsFilterDropdown({
  selectedRiskLevels,
  onRiskLevelsChange,
  daysSinceReview,
  onDaysSinceReviewChange,
  selectedBuilding,
  onBuildingChange,
  buildingOptions,
  selectedStation,
  onStationChange,
  stationOptions,
  showHidden,
  onShowHiddenChange,
  isLoading = false,
}: ResidentsFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Temp state for the dropdown (applied on "Apply" click)
  const [tempFilters, setTempFilters] = useState<ResidentsFilterState>({
    riskLevels: selectedRiskLevels,
    daysSinceReview,
    building: selectedBuilding,
    station: selectedStation,
    showHidden,
  });

  const activeFilterCount = getResidentsActiveFilterCount(
    selectedRiskLevels,
    daysSinceReview,
    selectedBuilding,
    selectedStation,
    showHidden,
  );

  const handleOpen = () => {
    // Sync temp state with current applied filters when opening
    setTempFilters({
      riskLevels: selectedRiskLevels,
      daysSinceReview,
      building: selectedBuilding,
      station: selectedStation,
      showHidden,
    });
    setIsOpen(true);
  };

  const handleApply = () => {
    onRiskLevelsChange(tempFilters.riskLevels);
    onDaysSinceReviewChange(tempFilters.daysSinceReview);
    onBuildingChange(tempFilters.building);
    onStationChange(tempFilters.station);
    onShowHiddenChange(tempFilters.showHidden);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleClearAll = () => {
    const cleared: ResidentsFilterState = {
      riskLevels: [],
      daysSinceReview: "",
      building: "all",
      station: [],
      showHidden: false,
    };
    setTempFilters(cleared);
  };

  const handleRiskLevelToggle = (level: RiskLevel) => {
    setTempFilters((prev) => {
      const levels = prev.riskLevels.includes(level)
        ? prev.riskLevels.filter((l) => l !== level)
        : [...prev.riskLevels, level];
      return { ...prev, riskLevels: levels };
    });
  };

  return (
    <div>
      <Button
        variant="outline"
        onClick={() => (isOpen ? handleCancel() : handleOpen())}
        disabled={isLoading}
        className="h-8 px-4 py-2 bg-white border border-solid border-[#228189] text-[#228189] hover:bg-gray-50 flex items-center gap-1 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#228189]" />
        ) : (
          <Filter className="w-4 h-4 fill-[#228189]" />
        )}
        <span className="text-sm font-bold">FILTER</span>
        {activeFilterCount > 0 && (
          <Badge
            variant="default"
            className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-[#228189] text-white"
          >
            {activeFilterCount}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={handleCancel}
          />

          {/* Filter Panel */}
          <div
            className="absolute top-full left-0 right-0 mx-4 mt-2 z-50 bg-white shadow-[0px_3px_14px_2px_rgba(0,0,0,0.12)] rounded-md border border-solid border-border p-6"
            style={{ transform: "none", position: "absolute" }}
          >
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#1B3647]">
                  Filters
                </h3>
                <Button
                  className="text-[#228189] uppercase p-0 hover:text-[#1a6b72]"
                  variant="ghost"
                  onClick={handleClearAll}
                >
                  Clear All
                </Button>
              </div>

              {/* Filter Fields */}
              <div className="flex flex-wrap gap-4">
                {/* Risk Level (multiselect) */}
                <FilterField
                  label={
                    tempFilters.riskLevels.length > 0
                      ? `Risk Level (${tempFilters.riskLevels.length})`
                      : "Risk Level"
                  }
                  type="multiselect"
                  options={RISK_LEVEL_OPTIONS}
                  value={tempFilters.riskLevels}
                  onChange={(value) => {
                    if (Array.isArray(value)) {
                      const level = value.find(
                        (v) => !tempFilters.riskLevels.includes(v as RiskLevel),
                      ) as RiskLevel | undefined;
                      const removed = tempFilters.riskLevels.find(
                        (v) => !value.includes(v),
                      );
                      if (level) handleRiskLevelToggle(level);
                      if (removed) handleRiskLevelToggle(removed);
                    }
                  }}
                />

                {/* Building */}
                <FilterField
                  label="Building"
                  type="singleselect"
                  options={buildingOptions}
                  value={tempFilters.building}
                  onChange={(v) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      building: v as string,
                    }))
                  }
                />

                {/* Station */}
                <FilterField
                  label={tempFilters.station.length > 0 ? `Station (${tempFilters.station.length})` : "Station"}
                  type="multiselect"
                  options={stationOptions}
                  value={tempFilters.station}
                  onChange={(v) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      station: v as string[],
                    }))
                  }
                />

                {/* Days Since Last Review */}
                <div className="space-y-2 w-full basis-full">
                  <Label className="text-sm font-medium text-[#3F4849]">
                    Days Since Last Review
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g. 30"
                    value={tempFilters.daysSinceReview}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        daysSinceReview: e.target.value.replace(/[^0-9]/g, ""),
                      }))
                    }
                    className="h-10 border-[#E0E0E0] w-[120px]"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center pt-4">
                {/* Show Hidden Residents Checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-show-hidden"
                    checked={tempFilters.showHidden}
                    onCheckedChange={(checked) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        showHidden: checked === true,
                      }))
                    }
                  />
                  <Label
                    htmlFor="filter-show-hidden"
                    className="cursor-pointer"
                  >
                    Show Hidden Residents
                  </Label>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApply}
                    className="px-6 bg-[#228189] hover:bg-[#1a6b72]"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
